import React, { useState, useEffect } from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { FileText, Eye, EyeOff, Wifi, WifiOff, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { getParasutSettings, updateParasutSettings, testParasutConnection } from '../../services/api';
import { toast } from 'sonner';

export const SettingsIntegrationTab = () => {
  const [parasutSettings, setParasutSettings] = useState({
    client_id: '', client_secret: '', firma_no: '', username: '', password: '', is_active: false, auto_e_archive: true,
  });
  const [showSecrets, setShowSecrets] = useState({ client_secret: false, password: false });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    getParasutSettings()
      .then(r => { setParasutSettings(prev => ({ ...prev, ...r.data })); setLoaded(true); })
      .catch(() => setLoaded(true));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateParasutSettings(parasutSettings);
      toast.success('Ayarlar kaydedildi');
    } catch (e) { toast.error(e.response?.data?.detail || 'Kaydetme hatası'); }
    finally { setSaving(false); }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const res = await testParasutConnection();
      if (res.data.status === 'success') toast.success('Bağlantı başarılı!');
      else toast.error(res.data.message || 'Bağlantı hatası');
    } catch (e) { toast.error(e.response?.data?.detail || 'Bağlantı testi başarısız'); }
    finally { setTesting(false); }
  };

  if (!loaded) return <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#6b6de3]" size={28} /></div>;

  return (
    <div className="animate-fade-in space-y-4">
      <h2 className="text-sm font-semibold text-[#8E8E93] uppercase tracking-wider">e-Fatura Entegrasyonları</h2>

      {/* Parasut Card */}
      <div className="ios-card space-y-4" data-testid="parasut-integration-card">
        <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between" data-testid="parasut-card-toggle">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center shadow-sm">
              <FileText size={20} className="text-white" />
            </div>
            <div className="text-left">
              <h3 className="font-semibold text-gray-900">Paraşüt</h3>
              <p className="text-xs text-[#8E8E93]">e-Fatura / e-Arşiv entegrasyonu</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-medium ${parasutSettings.is_active ? 'text-green-600' : 'text-[#8E8E93]'}`}>{parasutSettings.is_active ? 'Aktif' : 'Pasif'}</span>
            {expanded ? <ChevronUp size={18} className="text-[#8E8E93]" /> : <ChevronDown size={18} className="text-[#8E8E93]" />}
          </div>
        </button>

        {expanded && (
          <div className="space-y-4 pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">Entegrasyonu Aktifleştir</span>
              <Switch
                checked={parasutSettings.is_active}
                onCheckedChange={(v) => setParasutSettings(p => ({ ...p, is_active: v }))}
                data-testid="parasut-active-toggle"
              />
            </div>
            <div>
              <Label className="text-xs text-[#8E8E93]">Client ID</Label>
              <Input value={parasutSettings.client_id} onChange={e => setParasutSettings(p => ({ ...p, client_id: e.target.value }))}
                placeholder="Paraşüt Client ID" className="h-11 bg-[#F2F2F7] border-0 rounded-xl text-sm" data-testid="parasut-client-id" />
            </div>
            <div>
              <Label className="text-xs text-[#8E8E93]">Client Secret</Label>
              <div className="relative">
                <Input type={showSecrets.client_secret ? 'text' : 'password'} value={parasutSettings.client_secret}
                  onChange={e => setParasutSettings(p => ({ ...p, client_secret: e.target.value }))}
                  placeholder="Paraşüt Client Secret" className="h-11 bg-[#F2F2F7] border-0 rounded-xl text-sm pr-10" data-testid="parasut-client-secret" />
                <button type="button" onClick={() => setShowSecrets(p => ({ ...p, client_secret: !p.client_secret }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E8E93]">
                  {showSecrets.client_secret ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div>
              <Label className="text-xs text-[#8E8E93]">Firma No (Şirket ID)</Label>
              <Input value={parasutSettings.firma_no} onChange={e => setParasutSettings(p => ({ ...p, firma_no: e.target.value }))}
                placeholder="Örnek: 123456" className="h-11 bg-[#F2F2F7] border-0 rounded-xl text-sm" data-testid="parasut-firma-no" />
            </div>
            <div>
              <Label className="text-xs text-[#8E8E93]">Paraşüt E-posta (Kullanıcı Adı)</Label>
              <Input value={parasutSettings.username} onChange={e => setParasutSettings(p => ({ ...p, username: e.target.value }))}
                placeholder="ornek@email.com" className="h-11 bg-[#F2F2F7] border-0 rounded-xl text-sm" data-testid="parasut-username" />
            </div>
            <div>
              <Label className="text-xs text-[#8E8E93]">Paraşüt Şifre</Label>
              <div className="relative">
                <Input type={showSecrets.password ? 'text' : 'password'} value={parasutSettings.password}
                  onChange={e => setParasutSettings(p => ({ ...p, password: e.target.value }))}
                  placeholder="Paraşüt hesap şifresi" className="h-11 bg-[#F2F2F7] border-0 rounded-xl text-sm pr-10" data-testid="parasut-password" />
                <button type="button" onClick={() => setShowSecrets(p => ({ ...p, password: !p.password }))}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8E8E93]">
                  {showSecrets.password ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <p className="text-sm font-medium text-gray-700">Otomatik e-Arşiv / e-Fatura</p>
                <p className="text-xs text-[#8E8E93]">Fatura kesildikten sonra otomatik resmileştir</p>
              </div>
              <Switch
                checked={parasutSettings.auto_e_archive}
                onCheckedChange={(v) => setParasutSettings(p => ({ ...p, auto_e_archive: v }))}
                data-testid="parasut-auto-archive-toggle"
              />
            </div>

            <div className="flex gap-2 pt-2">
              <Button onClick={handleTestConnection} variant="outline" disabled={testing}
                className="flex-1 rounded-full text-sm h-11" data-testid="parasut-test-btn">
                {testing ? <Loader2 size={16} className="animate-spin mr-2" /> : (parasutSettings.is_active ? <Wifi size={16} className="mr-2" /> : <WifiOff size={16} className="mr-2" />)}
                Bağlantı Testi
              </Button>
              <Button onClick={handleSave} disabled={saving}
                className="flex-1 bg-[#6b6de3] rounded-full text-sm h-11" data-testid="parasut-save-btn">
                {saving ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Kaydet
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
